import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Check,
  Users,
  TrendingUp,
  Megaphone,
  CheckSquare,
  CircuitBoard,
  Layers,
  ShieldCheck,
  ArrowRight,
  Sparkles,
  MessageCircle,
  Briefcase,
  Building2,
  Cpu,
  ThumbsUp,
  Clock,
  Lock,
  LayoutGrid
} from 'lucide-react';
import { Button } from '../../Components/UI/button';
import MobileInput from '../../Components/Common/MobileInput';
import SahiLoader from '../../Components/Common/SahiLoader';
import { validatorObj, isLive } from '../../Utils/Utilities';
import useIsMobile from '../../Hooks/useIsMobile';
import { cn } from '../../Lib/Cn';
import SahiLongFooter from './SahiLongFooter';
import { motion } from 'framer-motion';

// Segment configurations tailored for industry networks
const SEGMENT_CONFIGS = {
  pcb: {
    segmentKey: 'pcb',
    metaTitle: 'SahiClub for PCB',
    badge: 'FOR PCB & ELECTRONICS BUSINESSES',
    title: 'The B2B community<br class="sm:hidden" /> & growth network<br class="sm:hidden" /> for <span class="text-sahi-green">PCB & Electronics</span>',
    subtitle: 'Connect with PCB manufacturers, EMS partners and active industrial buyers across India and manage your inquiries, contacts and tasks from one place.',
    footerDescription: "The B2B community & growth network built for PCB & electronics businesses across India.",
    footerTagline: "Made for India's electronics manufacturing belt",
    features: [
      {
        title: 'Connect & Network',
        subTitle: 'Engage with verified PCB manufacturers, EMS partners, and active buyers.',
        icon: Users,
        iconBg: 'sm:bg-sahi-orange-light',
        iconColor: 'text-sahi-orange'
      },
      {
        title: 'Market Products & News',
        subTitle: 'Showcase capabilities, products, facilities, and updates.',
        icon: Megaphone,
        iconBg: 'sm:bg-sahi-green-light',
        iconColor: 'text-sahi-green'
      },
      {
        title: 'Inquiries, Leads & Sales',
        subTitle: 'Receive buyer inquiries, and requirements to grow sales.',
        icon: TrendingUp,
        iconBg: 'sm:bg-sahi-orange-light',
        iconColor: 'text-sahi-orange'
      },
      {
        title: 'Built-in Tasks & CRM Tools',
        subTitle: 'Track customer follow-ups, pending sales, and team tasks with ease.',
        icon: LayoutGrid,
        iconBg: 'sm:bg-sahi-green-light',
        iconColor: 'text-sahi-green'
      }
    ],
    targetAudiences: [
      { name: 'PCB Manufacturers', desc: 'Single, Double & Multilayer Rigid/Flex' },
      { name: 'EMS & PCBA Providers', desc: 'SMT, DIP & Box Build Assembly' },
      { name: 'Component Vendors', desc: 'Passives, Actives & IC Distributors' },
      { name: 'PCB Materials & Laminates', desc: 'Copper Clad Laminates & Consumables' },
      { name: 'Machinery & Equipment', desc: 'Pick & Place, Reflow, AOI & Testing Tools' },
      { name: 'Hardware OEMs & Buyers', desc: 'Industrial, IoT, Automotive & EV Hardware' }
    ],
    pillars: [
      {
        icon: Users,
        badge: 'Community & Network',
        title: 'Electronics Industry Feed',
        desc: 'Connect with business owners, factory heads, and procurement teams. Share launches, expansions, and certifications with an audience that matters.'
      },
      {
        icon: Megaphone,
        badge: 'Product Marketing',
        title: 'Market Your Products & Facilities',
        desc: 'Put your PCB work in front of active buyers looking for reliable fabrication and assembly partners.'
      },
      {
        icon: TrendingUp,
        badge: 'Leads & Growth',
        title: 'Direct Buyer Inquiries',
        desc: 'Get discovered by buyers sourcing PCBs and components. Discuss specs and convert inquiries into recurring orders.'
      },
      {
        icon: CheckSquare,
        badge: 'Tasks & CRM',
        title: 'Tools to Manage & Close Deals',
        desc: 'Assign tasks, follow up on inquiries, and track customer history — so no opportunity slips through.'
      }
    ]
  }
};

const SegmentLanding = ({ segment = 'pcb' }) => {
  const isMobile = useIsMobile();
  const config = SEGMENT_CONFIGS[segment] || SEGMENT_CONFIGS.pcb;

  const [userDetails, setUserDetails] = useState({
    mobile: '',
    countryCode: '91'
  });
  const [bottomMobile, setBottomMobile] = useState('');
  const [isCCLoaded, setIsCCLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    document.title = config.metaTitle || "SahiClub for PCB";

    const fetchCountryCode = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        if (data && data.country_calling_code) {
          const cc = data.country_calling_code.replace('+', '');
          setUserDetails(prev => ({ ...prev, countryCode: cc }));
        }
      } catch (e) {
        setUserDetails(prev => ({ ...prev, countryCode: '91' }));
      } finally {
        setIsCCLoaded(true);
        setIsVisible(true);
      }
    };
    fetchCountryCode();
  }, [config.metaTitle]);

  const handleLogin = (customMobile = null) => {
    const rawMobile = customMobile !== null && typeof customMobile === 'string' ? customMobile : userDetails.mobile;
    const mobileToUse = rawMobile.trim();

    if (!mobileToUse) {
      toast.error('Please enter a contact number.');
      return;
    } else if (!validatorObj.validateMobileByCC(mobileToUse, userDetails.countryCode)) {
      toast.error('Please enter a valid contact number.');
      return;
    }

    const combinedMobile = `${userDetails.countryCode}_${mobileToUse}`;
    const encodedMobile = encodeURIComponent(combinedMobile);

    window.location.href = `/Web/login?i=${encodedMobile}&onboarding=${config.segmentKey}`;
  };

  const handleKeyPress = (e, customMobile = null) => {
    if (e.key === 'Enter') {
      handleLogin(customMobile);
    }
  };

  const scrollToHeroInput = () => {
    const inputEl = document.getElementById('heroMobileInput');
    if (inputEl) {
      inputEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      inputEl.focus();
    }
  };

  if (!isCCLoaded) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <SahiLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafbfc] text-[#1f2937] font-sans">
      {/* ─── Header ─── */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200/80 shadow-xs">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4 sm:px-6 lg:px-8 h-14 sm:h-16">
          <a href="/" className="w-32 sm:w-36 h-8 flex items-center">
            <img
              src={isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"}
              alt="SahiClub"
              className="max-h-full max-w-full object-contain"
            />
          </a>

          <div className="flex items-center gap-2.5 sm:gap-4">
            <Button
              onClick={scrollToHeroInput}
              className="text-xs sm:text-sm font-semibold bg-sahi-green hover:bg-white text-white hover:text-sahi-green border border-sahi-green hover:border-sahi-green h-8 sm:h-9 px-3 sm:px-4 rounded-lg cursor-pointer shadow-xs hover:shadow-md active:scale-95 transition-all duration-200"
            >
              Join Network
            </Button>
          </div>
        </div>
      </header>

      {/* ─── Hero Section ─── */}
      <section className="relative min-h-[calc(100dvh-3.5rem)] sm:min-h-[calc(100dvh-4rem)] flex flex-col justify-center py-8 sm:py-12 lg:py-16 overflow-hidden bg-gradient-to-b from-[#eaf5ee] via-[#f5faf6] to-white">
        {/* Subtle ambient light green radial glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(11,93,30,0.09),transparent_70%)] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full my-auto">
          
          {/* Top Row: Left Column (Badge + Heading + Subtitle) & Right Column (Signup Card) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Column on Desktop: Heading + Subtitle */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-7 flex flex-col justify-center text-center lg:text-left"
            >
              <div className="mb-4 sm:mb-6 hidden sm:flex justify-center lg:justify-start">
                <img
                  src={isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"}
                  alt="SahiClub"
                  className="h-10 sm:h-12 lg:h-14 w-auto object-contain transition-transform duration-200 hover:scale-105"
                />
              </div>
              <h1
                className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-sahi-dark-blue !leading-[1.2] tracking-tight mb-4"
                dangerouslySetInnerHTML={{ __html: config.title }}
              />
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {config.subtitle}
              </p>
            </motion.div>

            {/* Right Column: Clean & High-Conversion Signup Card */}
            <motion.div
              initial={{ opacity: 0, y: 28, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.65, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5 flex justify-end"
            >
              <div className="w-full max-w-md bg-white border border-gray-200/90 rounded-2xl p-6 sm:p-7 shadow-xs hover:shadow-lg transition-all duration-300 relative">
                <div className="mb-6">
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 leading-snug">
                    Get started in 30 seconds
                  </h3>
                  <p className="text-md text-gray-500">
                    Enter your mobile number
                  </p>
                </div>

                <div className="space-y-6">
                  <div>
                    <MobileInput
                      id="heroMobileInput"
                      value={userDetails.mobile}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val)) {
                          setUserDetails(prev => ({ ...prev, mobile: val }));
                        }
                      }}
                      placeholder="e.g. 9876543210"
                      className="w-full"
                      selectedCountryCode={userDetails.countryCode}
                      onCountryCodeChange={(newCC) =>
                        setUserDetails((prev) => ({ ...prev, countryCode: newCC }))
                      }
                      autoFocus={!isMobile}
                      onKeyPress={(e) => handleKeyPress(e)}
                    />
                    
                  </div>

                  <div className="flex flex-row gap-2.5 sm:gap-3">
                    <Button
                      onClick={() => handleLogin()}
                      className="group flex-1 bg-sahi-green hover:bg-white text-white hover:text-sahi-green border border-sahi-green hover:border-sahi-green font-semibold text-sm sm:text-base h-11 rounded-xl shadow-xs hover:shadow-md active:scale-[0.98] transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                    >
                      <span>Join Network Now</span>
                      <ArrowRight size={17} className="shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
                    </Button>
                    <Button
                      onClick={() => handleLogin()}
                      variant="secondary"
                      className="w-24 sm:w-28 border border-sahi-green text-sahi-green bg-white hover:bg-sahi-green hover:text-white font-semibold text-sm sm:text-base rounded-xl h-11 cursor-pointer transition-all duration-200 shadow-xs hover:shadow-md active:scale-[0.98]"
                    >
                      Login
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>

          {/* Bottom Row: 4 Feature Items (1-Col Left-aligned Centered List on Mobile, Cards on Desktop) */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.08, delayChildren: 0.25 }
              }
            }}
            className="w-fit mx-auto flex flex-col items-start gap-3.5 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:w-full sm:mx-0 sm:gap-4 lg:gap-5 mt-8 sm:mt-10 lg:mt-14"
          >
            {config.features.map((feat, idx) => {
              const IconComp = feat.icon || Check;
              return (
                <motion.div
                  key={idx}
                  variants={{
                    hidden: { opacity: 0, y: 14 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] }
                    }
                  }}
                  whileHover={isMobile ? undefined : { y: -4, transition: { duration: 0.2 } }}
                  className="group bg-transparent sm:bg-white rounded-none sm:rounded-2xl p-0 sm:p-5 lg:p-6 border-0 sm:border sm:border-gray-200/90 sm:hover:border-sahi-green/40 shadow-none sm:shadow-xs sm:hover:shadow-md transition-all duration-200 flex items-center sm:flex-col sm:justify-between sm:items-stretch gap-3 sm:gap-0 w-full cursor-default"
                >
                  <div className="flex items-center sm:block gap-3 sm:gap-0 w-full">
                    <div
                      className={cn(
                        "w-auto h-auto sm:w-10 sm:h-10 sm:rounded-lg flex items-center justify-center shrink-0 sm:mb-4 bg-transparent group-hover:scale-105 transition-transform duration-200",
                        feat.iconBg,
                        feat.iconColor
                      )}
                    >
                      <IconComp className="w-5 h-5 sm:w-5 sm:h-5 transition-transform duration-200 group-hover:scale-110" strokeWidth={2.2} />
                    </div>
                    <div>
                      <div className="text-sm sm:text-base font-bold text-gray-900 leading-snug group-hover:text-sahi-dark-blue transition-colors">
                        {feat.title}
                      </div>
                      {feat.subTitle && (
                        <p className="hidden lg:block text-xs text-gray-500 leading-relaxed mt-1.5">
                          {feat.subTitle}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

        </div>
      </section>

      {/* ─── Our Background Section ─── */}
      <section className="py-14 sm:py-20 bg-[#fafbfc] border-t border-gray-200/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-center max-w-3xl mx-auto mb-10 sm:mb-14"
          >
            <span className="text-xs font-bold text-sahi-green uppercase tracking-wider bg-sahi-green-light px-3 py-1 rounded-full border border-sahi-green/20 inline-block shadow-2xs hover:scale-105 transition-transform duration-200">
              OUR BACKGROUND
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-sahi-dark-blue mt-3 mb-3 leading-tight">
              Backed by the team who is building for Indian business since 1987
            </h2>
            {/* <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
              SahiClub is built by Biziverse, part of the Peach group — so you&apos;re not betting on an unproven startup.
            </p> */}
          </motion.div>

          {/* Biziverse & Peach Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            
            {/* Biziverse Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-white rounded-2xl border border-gray-200/90 border-t-4 border-t-sahi-orange p-6 sm:p-8 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="h-10 sm:h-12 lg:h-14 flex items-center mb-4 sm:mb-5">
                  <img
                    src={isLive() ? "/Web/assets/Images/Logos/biziverse.jpg" : "/assets/Images/Logos/biziverse.jpg"}
                    alt="Biziverse"
                    className="h-full w-auto object-contain max-h-12 sm:max-h-14"
                  />
                </div>
                <p className="text-xs sm:text-[14px] text-gray-600 leading-relaxed">
                  A govt-recognized startup in Gandhinagar, founded by US-returned entrepreneurs Ekta Shah and Parth Shah, on a mission to make ERP easy and affordable for every Indian business. Our team of dynamic, dedicated technocrats is backed by reputed investors with global experience — and you&apos;re always welcome to visit us at our green campus in Gandhinagar.
                </p>
              </div>
            </motion.div>

            {/* Peach Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-white rounded-2xl border border-gray-200/90 border-t-4 border-t-sahi-green p-6 sm:p-8 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="h-10 sm:h-12 lg:h-14 flex items-center mb-4 sm:mb-5">
                  <img
                    src={isLive() ? "/Web/assets/Images/Logos/peach.png" : "/assets/Images/Logos/peach.png"}
                    alt="Peach Technovations"
                    className="h-full w-auto object-contain max-h-12 sm:max-h-14"
                  />
                </div>
                <p className="text-xs sm:text-[14px] text-gray-600 leading-relaxed">
                  Peach&apos;s flagship product, Genie ERP offers Sales, CRM, Inventory, Production, HRM and Accounting in one cloud platform for growth-oriented manufacturers and traders. Peach also holds an international award for G2C service delivery at &ldquo;Jan Seva Kendra&rdquo; civic centers, and has helped establish nationwide e-Governance models serving millions of citizens.
                </p>
              </div>
            </motion.div>

          </div>

        </div>
      </section>

      {/* ─── Who's In The Network Section ─── */}
      <section className="py-14 sm:py-16 bg-[#fafbfc] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-sahi-dark-blue mb-2">
              Who is Connecting in the Network?
            </h2>
            <p className="text-xs sm:text-sm text-gray-600">
              From raw laminate suppliers to component traders and hardware buyers, all major players in the electronics ecosystem collaborate on SahiClub.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {config.targetAudiences.map((aud, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-200 rounded-lg p-3 text-center shadow-2xs hover:border-sahi-green/50 transition-colors"
              >
                <div className="text-xs font-bold text-gray-900 mb-0.5">{aud.name}</div>
                <div className="text-[10px] text-gray-500 leading-tight">{aud.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Bottom High-Conversion CTA Banner ─── */}
      <section className="bg-[#fafbfc] py-0 sm:py-16">
        <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-none sm:rounded-2xl bg-gradient-to-br from-sahi-dark-blue to-[#073628] text-white py-12 sm:py-16 px-8 sm:px-12 text-center shadow-lg relative overflow-hidden"
          >
            {/* Subtle glow circle decoration */}
            <div className="absolute -top-24 -left-24 w-72 h-72 bg-sahi-green/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none" />

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-3 relative z-10">
              Ready to Market Your Products & Win Inquiries?
            </h2>
            <p className="text-sm sm:text-base text-gray-300 max-w-2xl mx-auto mb-8 relative z-10">
              Join hundreds of PCB manufacturers, EMS assemblers, and electronics businesses.
            </p>

            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="w-full max-w-xl mx-auto bg-white p-2 sm:p-2.5 rounded-lg sm:rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center relative z-10"
            >
              <div className="flex-1 min-w-0">
                <MobileInput
                  id="bottomMobileInput"
                  value={bottomMobile}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                      setBottomMobile(val);
                    }
                  }}
                  placeholder="Enter mobile number"
                  className="w-full text-gray-900"
                  selectedCountryCode={userDetails.countryCode}
                  onCountryCodeChange={(newCC) =>
                    setUserDetails((prev) => ({ ...prev, countryCode: newCC }))
                  }
                  onKeyPress={(e) => handleKeyPress(e, bottomMobile)}
                />
              </div>
              <Button
                onClick={() => handleLogin(bottomMobile || userDetails.mobile)}
                className="group bg-sahi-green hover:bg-white text-white hover:text-sahi-green border border-sahi-green hover:border-sahi-green font-bold text-sm sm:text-base px-6 h-11 shrink-0 cursor-pointer transition-all duration-200 shadow-xs hover:shadow-md active:scale-95 flex items-center justify-center gap-1.5"
              >
                <span>Get Started Now</span>
                <ArrowRight size={16} className="shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <div id="footer">
        <SahiLongFooter
          description={config.footerDescription}
          tagline={config.footerTagline}
        />
      </div>
    </div>
  );
};

export default SegmentLanding;
